import { Fade, Tooltip } from '@mui/material';
import { type GridRenderCellParams, GridRowParams, renderActionsCell } from '@mui/x-data-grid-premium';
import {
  RiCloseLine,
  RiDeleteBinLine,
  RiErrorWarningLine,
  RiFileCopyLine,
  RiLockLine,
  RiMore2Line,
} from '@remixicon/react';
import { deviceType } from 'detect-it';
import { type ComponentPropsWithoutRef, forwardRef, useMemo } from 'react';
import { AgDropdownItem, AgDropdownMenu } from 'src/components/ui/AgDropdownMenu';
import { FlagButton } from 'src/modules/entities/notes';
import { useGridCore } from 'src/providers/GridCoreProvider';
import { entityTypes } from 'src/utils/entityTypes';

/** GridActionsCell clones ButtonBase props onto every action; `touchRippleRef` must not reach a DOM node. The ref forward keeps Fade's transition working. */
const ActionSlot = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'> & { touchRippleRef?: unknown }>(
  ({ touchRippleRef: _touchRippleRef, ...rest }, ref) => <div ref={ref} {...rest} />
);
ActionSlot.displayName = 'ActionSlot';

/** One `icon-sm` button footprint (AgButton). EVERY slot is this wide, or the column goes ragged. */
const ACTION_SLOT_CLASS = 'h-[32px] w-[32px] shrink-0';

/** Holds an absent action's footprint so its sibling keeps the same x-offset on every row. */
const EmptyActionSlot = () => <ActionSlot aria-hidden className={ACTION_SLOT_CLASS} />;

type ColumnActions = {
  deleteRow?: (params) => void;
  clearRow?: (params) => void;
  flagRow?: (value: boolean, params) => Promise<unknown>;
  duplicateRow?: (params) => void;
  isFlagged?: boolean;
  lockedMessage?: string;
  errorMessage?: string;
};

type ActionResolver = (params) => ColumnActions;

/**
 * `createNote` accepts `workOrderId`, NOT `growerProductId` (the Prisma column keeps its old
 * name) while grid rows arrive as `__typename: 'GrowerProduct'` — and GraphQL silently DISCARDS
 * an undeclared variable, so the note would persist with no parent.
 */
const NOTE_TARGET_KEY_OVERRIDES: Record<string, string> = { growerProductId: 'workOrderId' };

const getDefaultFlagCommentTarget = (row: GridRowParams) => {
  const idKey = entityTypes[row.row?.__typename]?.id;
  return { [NOTE_TARGET_KEY_OVERRIDES[idKey] ?? idKey]: row.row?.id };
};

/** An `actions` column's renderCell: a group footer row has no row model, so it gets no actions instead of a throw. */
export const renderRowActionsCell = (params: GridRenderCellParams) =>
  params.rowNode.type === 'footer' ? null : renderActionsCell(params);

export default function columnActions(
  resolver: ActionResolver,
  options?: {
    showOnHover?: boolean;
    /**
     * Reserve the leading flag slot on rows that cannot flag. ONLY for columns where some rows
     * flag and others do not (PO/SO templates); on a column that never flags it would shift
     * every trailing icon right by one slot.
     */
    reserveFlagSlot?: boolean;
    getFlagCommentTarget?: (row: GridRowParams) => { [key: string]: number };
    /** When provided, row action elements get domain-prefixed testids derived from the row. */
    getRowTestIds?: (row: GridRowParams) => {
      flag?: string;
      menu?: string;
      delete?: string;
      duplicate?: string;
      clear?: string;
    };
  }
) {
  const ReactiveRowActions = ({ params }: { params: GridRowParams }) => {
    const { deleteRow, clearRow, flagRow, duplicateRow, isFlagged, lockedMessage, errorMessage } = resolver(params);
    const { showOnHover } = options || {};
    const rowTestIds = options?.getRowTestIds?.(params);
    const isHovering = useGridCore((store) => store.hoveredRowId === params.id);

    return useMemo(() => {
      if (typeof params.id === 'string' && params.id.startsWith('auto-generated')) {
        return [];
      }

      const isDraft = params.row?.isNew || params.row?.isDraft;

      // Leading slot — the flag.
      const flagSlot = flagRow ? (
        <FlagButton
          key="flag"
          flagged={isFlagged}
          handleFlagChange={async (value) => await flagRow(value, params)}
          id={options?.getFlagCommentTarget?.(params) || getDefaultFlagCommentTarget(params)}
          buttonProps={{
            variant: 'ghost',
            size: 'icon-sm',
            className: 'hover:bg-transparent',
            excludeFromTabOrder: true,
          }}
          data-testid={rowTestIds?.flag}
        />
      ) : null;

      // Absent only where the column never flags — then nothing leads and the trailing icon stays put.
      const leadingSlot = flagSlot ?? (options?.reserveFlagSlot ? <EmptyActionSlot key="flag-empty" /> : null);

      // Locked state — lock icon takes the trailing slot, no menu
      if (lockedMessage) {
        return [
          leadingSlot,
          <Tooltip key="lock" title={lockedMessage}>
            <Fade in={isHovering}>
              <ActionSlot>
                <div className={`${ACTION_SLOT_CLASS} flex items-center justify-center`}>
                  <RiLockLine size={20} className="text-neutral-7" />
                </div>
              </ActionSlot>
            </Fade>
          </Tooltip>,
        ];
      }

      // Error state — error icon takes the trailing slot, no menu
      if (errorMessage) {
        return [
          leadingSlot,
          <Tooltip key="error" title={errorMessage}>
            <Fade in={isHovering}>
              <ActionSlot>
                <div className={`${ACTION_SLOT_CLASS} flex items-center justify-center`}>
                  <RiErrorWarningLine size={20} className="text-red-text" />
                </div>
              </ActionSlot>
            </Fade>
          </Tooltip>,
        ];
      }

      // Collect menu actions
      const menuItems = [];

      if (duplicateRow && !isDraft) {
        menuItems.push(
          <AgDropdownItem
            key="duplicate"
            icon={<RiFileCopyLine size={16} />}
            onAction={() => duplicateRow(params)}
            data-testid={rowTestIds?.duplicate}
          >
            Duplicate
          </AgDropdownItem>
        );
      }

      if (deleteRow && !isDraft) {
        menuItems.push(
          <AgDropdownItem
            key="delete"
            destructive
            icon={<RiDeleteBinLine size={16} />}
            onAction={() => deleteRow(params)}
            data-testid={rowTestIds?.delete}
          >
            Delete
          </AgDropdownItem>
        );
      } else if (clearRow && isDraft) {
        menuItems.push(
          <AgDropdownItem
            key="clear"
            destructive
            icon={<RiCloseLine size={16} />}
            onAction={() => clearRow(params)}
            data-testid={rowTestIds?.clear}
          >
            Clear
          </AgDropdownItem>
        );
      }

      // Trailing slot — dot menu with all non-flag actions
      const menuSlot =
        menuItems.length > 0 ? (
          <Fade key="menu" in={!showOnHover || deviceType !== 'mouseOnly' || isHovering}>
            <ActionSlot>
              <AgDropdownMenu
                size="icon-sm"
                variant="ghost"
                shape="rounded"
                align="end"
                className="hover:bg-transparent"
                icon={<RiMore2Line size={18} />}
                items={<>{menuItems}</>}
                excludeFromTabOrder
                data-testid={rowTestIds?.menu}
              />
            </ActionSlot>
          </Fade>
        ) : null;

      // Guard on the REAL actions: a reserved leading slot must not keep an empty cell alive.
      if (!flagSlot && !menuSlot) {
        return [];
      }

      // Pad the trailing slot only when something leads it, so the pair keeps a constant width.
      const trailingSlot = menuSlot ?? (leadingSlot ? <EmptyActionSlot key="menu-empty" /> : null);

      return [leadingSlot, trailingSlot].filter(Boolean);
    }, [
      params,
      clearRow,
      deleteRow,
      flagRow,
      duplicateRow,
      lockedMessage,
      showOnHover,
      isFlagged,
      isHovering,
      errorMessage,
      options,
      rowTestIds,
    ]);
  };

  return (params: GridRowParams) => [<ReactiveRowActions key="actions" params={params} />];
}
